export const GUEST_XP_KEY = 'bigplays:guestXp';
export const GUEST_CHAT_COUNT_KEY = 'bigplays:guestHallChatCount';
export const GUEST_HALL_CHAT_LIMIT = 5;
export const SHOW_SIGNIN_PROMPT = 'bigplays:show-signin-prompt';
export const XP_LEADER_KEY = 'bigplays:xpLeader';
export const XP_EVENT = 'bigplays:xp';
export const XP_PROMPT_KEY = 'bigplays:xpPrompt';
export const TICK_MS = 60 * 1000;
export const MIN_TICK_MS = 50 * 1000;
export const PROMPT_INTERVAL_MS = 30 * 60 * 1000;

let lastKnownXp = null;
let lastKnownHallXp = null;

function readGuestRecord() {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_XP_KEY) || '{}');
    return {
      xp: Math.max(0, Math.floor(Number(raw.xp) || 0)),
      lastAwardAt: Number(raw.lastAwardAt) || 0
    };
  } catch {
    return { xp: 0, lastAwardAt: 0 };
  }
}

export function getGuestXp() {
  return readGuestRecord().xp;
}

export function getGuestHallChatCount() {
  try {
    return Math.max(0, Math.floor(Number(localStorage.getItem(GUEST_CHAT_COUNT_KEY)) || 0));
  } catch {
    return 0;
  }
}

export function setGuestHallChatCount(n) {
  const next = Math.max(0, Math.floor(Number(n) || 0));
  try {
    localStorage.setItem(GUEST_CHAT_COUNT_KEY, String(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function bumpGuestHallChatCount() {
  return setGuestHallChatCount(getGuestHallChatCount() + 1);
}

export function requestSignInPrompt(reason) {
  window.dispatchEvent(new CustomEvent(SHOW_SIGNIN_PROMPT, {
    detail: { reason: reason || null }
  }));
}

export function getLastKnownXp() {
  return lastKnownXp;
}

export function getLastKnownHallXp() {
  return lastKnownHallXp;
}

/** Broadcast the current XP so every UI (hall bar, avatar menu, profile) stays in sync. */
export function publishXp(xp, hallXp) {
  const n = Math.max(0, Math.floor(Number(xp) || 0));
  const nextHallXp = typeof hallXp === 'number'
    ? Math.max(0, Math.floor(hallXp))
    : lastKnownHallXp;
  if (lastKnownXp === n && lastKnownHallXp === nextHallXp) return n;
  lastKnownXp = n;
  if (typeof hallXp === 'number') lastKnownHallXp = nextHallXp;
  window.dispatchEvent(new CustomEvent(XP_EVENT, {
    detail: { xp: n, hallXp: lastKnownHallXp }
  }));
  return n;
}

export async function fetchAccountXp(hallSlug) {
  try {
    const params = hallSlug ? `?hall=${encodeURIComponent(hallSlug)}` : '';
    const res = await fetch(`/api/xp${params}`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    if (typeof data.xp !== 'number') return null;
    return publishXp(data.xp, data.hallXp);
  } catch {
    return null;
  }
}

export function setGuestXp(xp, lastAwardAt = Date.now()) {
  const next = {
    xp: Math.max(0, Math.floor(Number(xp) || 0)),
    lastAwardAt
  };
  localStorage.setItem(GUEST_XP_KEY, JSON.stringify(next));
  return publishXp(next.xp);
}

export function addGuestMinute() {
  const rec = readGuestRecord();
  const now = Date.now();
  if (rec.lastAwardAt && now - rec.lastAwardAt < MIN_TICK_MS) {
    return rec.xp;
  }
  return setGuestXp(rec.xp + 1, now);
}

export function clearGuestXp() {
  localStorage.removeItem(GUEST_XP_KEY);
}

let mergeInFlight = null;

export async function mergeGuestXpToAccount() {
  if (mergeInFlight) return mergeInFlight;
  mergeInFlight = (async () => {
    const xp = getGuestXp();
    if (xp <= 0) return null;
    try {
      const res = await fetch('/api/xp/merge-guest', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ xp })
      });
      if (!res.ok) return null;
      const data = await res.json();
      clearGuestXp();
      if (typeof data.xp !== 'number') return null;
      return publishXp(data.xp);
    } catch {
      return null;
    }
  })().finally(() => {
    mergeInFlight = null;
  });
  return mergeInFlight;
}

function readLeader() {
  try {
    return JSON.parse(localStorage.getItem(XP_LEADER_KEY) || 'null');
  } catch {
    return null;
  }
}

/** Only one visible tab awards XP. Hidden tabs release leadership. */
export function createXpTabLeader(onChange) {
  const tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const HEARTBEAT_MS = 2000;
  const STALE_MS = 5000;
  let isLeader = false;

  const publish = (next) => {
    if (next !== isLeader) {
      isLeader = next;
      onChange(isLeader);
    }
  };

  const beat = () => {
    if (document.visibilityState !== 'visible') {
      const cur = readLeader();
      if (cur?.id === tabId) localStorage.removeItem(XP_LEADER_KEY);
      publish(false);
      return;
    }

    const cur = readLeader();
    if (!cur || Date.now() - cur.ts > STALE_MS || cur.id === tabId) {
      localStorage.setItem(XP_LEADER_KEY, JSON.stringify({ id: tabId, ts: Date.now() }));
      publish(true);
      return;
    }
    publish(cur.id === tabId);
  };

  beat();
  const timer = setInterval(beat, HEARTBEAT_MS);
  document.addEventListener('visibilitychange', beat);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', beat);
    const cur = readLeader();
    if (cur?.id === tabId) localStorage.removeItem(XP_LEADER_KEY);
  };
}

export function steamLoginHref(returnTo) {
  const dest = returnTo || window.location.pathname + window.location.search;
  return `/api/auth/steam?returnTo=${encodeURIComponent(dest)}`;
}

export function googleLoginHref() {
  const returnTo = window.location.pathname + window.location.search;
  return `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
}

export function isCommonsHallSlug(slug) {
  return !slug || String(slug).toLowerCase() === 'commons';
}

/** Commons / profile: total. Other halls: `total (hallXp)`. */
export function formatXpLabel(total, hallXp, hallSlug) {
  const t = Math.max(0, Math.floor(Number(total) || 0));
  if (isCommonsHallSlug(hallSlug) || typeof hallXp !== 'number') {
    return `${t} XP`;
  }
  return `${t} (${Math.max(0, Math.floor(hallXp))})`;
}
